/**
 * JIRA Configuration Page
 *
 * Company Admin page for configuring JIRA Cloud integration.
 * Allows admins to set up JIRA credentials, test connection,
 * and view available JIRA projects.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import {
  getJiraConfiguration,
  createOrUpdateJiraConfiguration,
  testJiraConnection,
  getJiraProjects,
  deleteJiraConfiguration,
  JiraConfiguration as JiraConfigType
} from '../services/jiraService';

export default function JiraConfiguration() {
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const [config, setConfig] = useState<JiraConfigType | null>(null);
  const [formData, setFormData] = useState({
    jira_domain: '',
    jira_email: '',
    jira_api_token: '',
    webhook_secret: ''
  });

  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [jiraProjects, setJiraProjects] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadConfiguration();
  }, []);

  async function loadConfiguration() {
    try {
      setLoading(true);
      setError(null);
      const response = await getJiraConfiguration(token!);

      if (response.success && response.data) {
        setConfig(response.data);
        setFormData({
          jira_domain: response.data.jira_domain,
          jira_email: response.data.jira_email,
          jira_api_token: '', // Don't show stored token
          webhook_secret: response.data.webhook_secret || ''
        });
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        setError('Failed to load JIRA configuration');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    setTestResult(null);

    try {
      // Only send API token if it has been provided (not empty)
      const updateData: any = {
        jira_domain: formData.jira_domain,
        jira_email: formData.jira_email,
        webhook_secret: formData.webhook_secret || undefined
      };

      // Only include API token if user has entered one (for update scenarios)
      if (formData.jira_api_token && formData.jira_api_token.trim()) {
        updateData.jira_api_token = formData.jira_api_token;
      } else if (!config) {
        // For new configurations, API token is required
        setError('API token is required for new configurations');
        setSaving(false);
        return;
      }

      const response = await createOrUpdateJiraConfiguration(updateData, token!);

      if (response.success) {
        setSuccess(config ? 'Configuration updated successfully' : 'Configuration created successfully');
        setConfig(response.data);
        // Clear the API token field after save
        setFormData(prev => ({ ...prev, jira_api_token: '' }));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  }

  async function handleTestConnection() {
    setTesting(true);
    setTestResult(null);
    setError(null);

    try {
      const response = await testJiraConnection(token!);
      setTestResult({
        success: response.success,
        message: response.message || 'Connection successful'
      });
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.response?.data?.error || 'Connection failed'
      });
    } finally {
      setTesting(false);
    }
  }

  async function handleLoadProjects() {
    setLoadingProjects(true);
    setError(null);

    try {
      const response = await getJiraProjects(token!);
      if (response.success) {
        setJiraProjects(response.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load JIRA projects');
    } finally {
      setLoadingProjects(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm('Are you sure you want to delete the JIRA configuration? This will deactivate all project mappings.')) {
      return;
    }

    try {
      setError(null);
      await deleteJiraConfiguration(token!);
      setSuccess('JIRA configuration deleted successfully');
      setConfig(null);
      setFormData({
        jira_domain: '',
        jira_email: '',
        jira_api_token: '',
        webhook_secret: ''
      });
      setJiraProjects([]);
      setTestResult(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete configuration');
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500 dark:text-gray-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg mb-6 p-6">
          <div className="flex justify-between items-center">
            <div>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm text-blue-100 hover:text-white mb-2 inline-flex items-center transition-colors"
              >
                ← Back to Dashboard
              </button>
              <h1 className="text-3xl font-bold text-white">JIRA Integration</h1>
              <p className="mt-2 text-blue-100">
                Configure JIRA Cloud integration for your company
              </p>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="mb-4 p-4 rounded-md bg-red-50 border border-red-200">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Configuration Form */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">
              {config ? 'Update' : 'Setup'} JIRA Configuration
            </h2>

            <form onSubmit={handleSave}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* JIRA Domain */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    JIRA Domain <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">https://</span>
                    <input
                      type="text"
                      value={formData.jira_domain}
                      onChange={(e) =>
                        setFormData({ ...formData, jira_domain: e.target.value })
                      }
                      placeholder="your-company.atlassian.net"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Your JIRA Cloud domain (e.g., your-company.atlassian.net)
                  </p>
                </div>

                {/* JIRA Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    JIRA Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.jira_email}
                    onChange={(e) =>
                      setFormData({ ...formData, jira_email: e.target.value })
                    }
                    placeholder="user@example.com"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Email address associated with your JIRA account
                  </p>
                </div>

                {/* API Token */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    API Token <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={formData.jira_api_token}
                    onChange={(e) =>
                      setFormData({ ...formData, jira_api_token: e.target.value })
                    }
                    placeholder={config ? 'Enter new token to update' : 'Enter API token'}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required={!config}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    <a
                      href="https://id.atlassian.com/manage-profile/security/api-tokens"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-700"
                    >
                      Generate an API token
                    </a>{' '}
                    from your Atlassian account
                  </p>
                </div>

                {/* Webhook Secret (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Webhook Secret (Optional)
                  </label>
                  <input
                    type="password"
                    value={formData.webhook_secret}
                    onChange={(e) =>
                      setFormData({ ...formData, webhook_secret: e.target.value })
                    }
                    placeholder="Optional webhook authentication secret"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Optional secret for validating incoming webhooks from JIRA
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving...' : config ? 'Update Configuration' : 'Save Configuration'}
                </button>

                {config && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-full text-red-600 dark:text-red-400 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Delete Configuration
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Test Connection */}
          {config && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">
                Test Connection
              </h2>

              <button
                onClick={handleTestConnection}
                disabled={testing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {testing ? 'Testing...' : 'Test Connection'}
              </button>

              {testResult && (
                <div
                  className={`mt-4 p-4 rounded-md ${
                    testResult.success
                      ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                      : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                  }`}
                >
                  <p
                    className={`text-sm ${
                      testResult.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
                    }`}
                  >
                    {testResult.message}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* View JIRA Projects */}
          {config && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                  Available JIRA Projects
                </h2>
                <button
                  onClick={handleLoadProjects}
                  disabled={loadingProjects}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {loadingProjects ? 'Loading...' : 'Load Projects'}
                </button>
              </div>

              {jiraProjects.length > 0 && (
                <div className="space-y-2">
                  {jiraProjects.map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-md"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-50">
                          {project.name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Key: {project.key}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {jiraProjects.length === 0 && !loadingProjects && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Click "Load Projects" to view your JIRA projects
                </p>
              )}
            </div>
          )}

          {/* Webhook Setup (for bi-directional sync) */}
          {config && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <svg
                  className="w-5 h-5 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                  />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                  Webhook Setup (Bi-directional Sync)
                </h2>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Configure webhooks in JIRA to automatically sync changes from JIRA back to Nivoon.
                This enables real-time bi-directional synchronization.
              </p>

              <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-md border border-indigo-200 dark:border-indigo-800 p-4 mb-4">
                <h4 className="text-sm font-semibold text-indigo-900 dark:text-indigo-200 mb-2">
                  Webhook URL
                </h4>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 border border-indigo-300 dark:border-indigo-600 rounded-md text-sm text-gray-900 dark:text-gray-50 font-mono">
                    {window.location.origin}/api/webhooks/jira
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/api/webhooks/jira`);
                      setSuccess('Webhook URL copied to clipboard!');
                      setTimeout(() => setSuccess(null), 3000);
                    }}
                    className="px-3 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 transition-colors"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                  Setup Instructions:
                </h4>
                <ol className="text-sm text-gray-700 dark:text-gray-300 space-y-2 list-decimal list-inside">
                  <li>
                    Go to your JIRA project settings → System → WebHooks
                    <br />
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-5">
                      Or visit:{' '}
                      <code className="text-xs">
                        https://{formData.jira_domain}/plugins/servlet/webhooks
                      </code>
                    </span>
                  </li>
                  <li>
                    Click "Create a WebHook"
                  </li>
                  <li>
                    Configure the webhook:
                    <ul className="mt-1 ml-5 space-y-1 list-disc list-inside text-xs text-gray-600 dark:text-gray-400">
                      <li>
                        <strong>Name:</strong> Nivoon Integration
                      </li>
                      <li>
                        <strong>Status:</strong> Enabled
                      </li>
                      <li>
                        <strong>URL:</strong> Copy the webhook URL above
                      </li>
                      <li>
                        <strong>Events:</strong> Select "Issue" and check:
                        <ul className="ml-5 mt-1 space-y-1">
                          <li>✓ Issue updated</li>
                          <li>✓ Issue deleted</li>
                        </ul>
                      </li>
                    </ul>
                  </li>
                  <li>
                    (Optional) Set the webhook secret to match the value configured above
                  </li>
                  <li>
                    Click "Create" to save the webhook
                  </li>
                  <li>
                    Test the webhook by updating a synced JIRA issue and verifying the changes appear in Nivoon
                  </li>
                </ol>
              </div>

              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                <div className="flex gap-2">
                  <svg
                    className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Important Notes
                    </p>
                    <ul className="mt-1 text-xs text-yellow-700 dark:text-yellow-300 space-y-1 list-disc list-inside">
                      <li>
                        Webhooks enable real-time sync from JIRA to Nivoon
                      </li>
                      <li>
                        Only synced requirements will receive updates from JIRA
                      </li>
                      <li>
                        Platform Admins can view webhook logs at /admin/jira-webhooks
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Setup Instructions */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-6">
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
              Setup Instructions
            </h3>
            <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-2 list-decimal list-inside">
              <li>
                Generate an API token from your{' '}
                <a
                  href="https://id.atlassian.com/manage-profile/security/api-tokens"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-900 dark:hover:text-blue-100"
                >
                  Atlassian account security settings
                </a>
              </li>
              <li>Enter your JIRA domain, email, and API token above</li>
              <li>Click "Save Configuration" to store your settings</li>
              <li>Test the connection to verify everything works</li>
              <li>
                Once configured, you can map Nivoon projects to JIRA projects
                from the project settings page
              </li>
            </ol>
          </div>
        </div>
      </div>
    </Layout>
  );
}
